/**
 * T103 (GAP-1 closed 2026-09-15 — `GET /api/catalog/skus` verified live against the running
 * gateway, paged, `isIncomplete` on every row per FR-013). Loader/view shape copied from
 * `../brands/List.tsx` and `../products/List.tsx`, this migration's own All/Active/Inactive
 * precedent for a catalogue list with no `inactiveOnly` server filter.
 *
 * One real difference from brands/products: `GET /skus` DOES take a server `q` (verified against
 * `CatalogueController.ListSkus`/`ListSkusAsync`), so this list searches the whole set through
 * `SearchInput`, the same server-search idiom `inventory/List.tsx` uses — never `TableSearch`
 * (which would only search the page already on screen).
 */
import { Link, useLoaderData, useNavigate, useOutletContext, useSearchParams, type LoaderFunctionArgs } from "react-router-dom";
import { PiPlusBold as Plus } from "react-icons/pi";
import { Alert, Button } from "rizzui";
import { EmptyState } from "@steelyard/theme";
import { PagedTable } from "../../../components/table/data-table";
import { ListHeader } from "../../../components/table/list-header";
import { SearchInput } from "../../../components/table/search-input";
import { activeStatusOptions } from "../../../components/table/toolbar";
import { skuColumns } from "./columns";
import { CATALOG_MAX_PAGE_SIZE, deactivateSku, listSkus, reactivateSku, type SkuListItem } from "../../../lib/services/catalog";
import { can } from "../../../lib/permissions";
import { activeViewParams, hrefWith, readActiveView, readPaging } from "../../../lib/paging";
import type { PermissionState, AccountSummary } from "../../../types";

/**
 * `GET /skus` filters on `activeOnly` and `q` server-side, but has no `inactiveOnly` — same
 * ceiling as brands/products/categories above it in this file tree. Inactive reads the whole set
 * (narrowed by `q` if one is typed) up to the API's 200-row snapshot ceiling and keeps the rows
 * marked `isActive: false`; All and Active are the API's own `totalCount`s, never "all − active".
 */
export async function skusListLoader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url);
  const { page, pageSize } = readPaging(url.searchParams);
  const view = readActiveView(url.searchParams);
  const q = url.searchParams.get("q") ?? undefined;

  if (view === "inactive") {
    const [every, active] = await Promise.all([
      listSkus({ page: 1, pageSize: CATALOG_MAX_PAGE_SIZE, activeOnly: false, q }),
      listSkus({ page: 1, pageSize: 1, activeOnly: true, q }).catch(() => null),
    ]);
    const items = every.items.filter((sku) => !sku.isActive);
    const complete = every.items.length >= Number(every.totalCount);
    return {
      result: { items, page: 1, pageSize: items.length, totalCount: items.length },
      view,
      complete,
      q,
      counts: { all: Number(every.totalCount), active: active ? Number(active.totalCount) : null, inactive: complete ? items.length : null },
    };
  }

  const [result, other] = await Promise.all([
    listSkus({ page, pageSize, activeOnly: view === "active", q }),
    listSkus({ page: 1, pageSize: 1, activeOnly: view !== "active", q }).catch(() => null),
  ]);
  if (!result) throw new Error("The API returned no body for the SKU list.");
  const otherCount = other ? Number(other.totalCount) : null;
  return {
    result,
    view,
    complete: true,
    q,
    counts: {
      all: view === "active" ? otherCount : Number(result.totalCount),
      active: view === "active" ? Number(result.totalCount) : otherCount,
      inactive: null,
    },
  };
}

export default function SkusList() {
  const { result, view, complete, q, counts } = useLoaderData() as Awaited<ReturnType<typeof skusListLoader>>;
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const canWrite = can(permissionState, "catalogue.administer");
  const query = (over: Record<string, string | undefined>) => hrefWith("/catalogue/skus", searchParams, over);

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        title="SKUs"
        count={result.totalCount}
        description="Every sellable SKU in the catalogue. Incomplete is the API's own flag — a SKU still missing a conversion factor or a sellable unit."
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Catalogue" }, { name: "SKUs" }]}
      />

      {!complete ? (
        <Alert color="warning" variant="flat">
          Inactive shows the inactive SKUs among the first {CATALOG_MAX_PAGE_SIZE} of {counts.all}. The catalogue
          service has no inactive-only filter yet, so any beyond that are not listed here — narrow the search to
          check the rest.
        </Alert>
      ) : null}

      <PagedTable<SkuListItem>
        actions={
          canWrite ? (
            <Link to="/catalogue/skus/new">
              <Button as="span">
                <Plus aria-hidden className="me-1.5 size-4" />
                New SKU
              </Button>
            </Link>
          ) : null
        }
        result={result}
        caption="SKUs, with brand, category, status and completeness"
        columns={skuColumns(canWrite ? (sku, active) => (active ? reactivateSku : deactivateSku)(sku.id) : undefined)}
        getRowId={(row) => row.id}
        status={{
          label: "Status",
          options: activeStatusOptions(view, (next) => hrefWith("/catalogue/skus", searchParams, activeViewParams(next)), counts),
        }}
        // `q` is a server param (verified against `ListSkusAsync`), so this searches every SKU, not
        // only the page on screen.
        search={
          <SearchInput
            value={q}
            placeholder={view === "inactive" ? "Search inactive SKUs…" : "Search by code or name…"}
            onSearch={(next) => navigate(query({ q: next, page: undefined }))}
          />
        }
        clear={{ active: Boolean(q) || view !== "active", href: query({ q: undefined, ...activeViewParams("active") }) }}
        empty={
          <EmptyState
            title={q ? "No SKUs matched" : view === "active" ? "No active SKUs" : view === "inactive" ? "No inactive SKUs" : "No SKUs yet"}
            action={
              q ? (
                <Link to="/catalogue/skus">
                  <Button as="span" variant="outline" size="sm">
                    Clear the search
                  </Button>
                </Link>
              ) : canWrite && view === "all" ? (
                <Link to="/catalogue/skus/new">
                  <Button as="span" size="sm">
                    <Plus aria-hidden />
                    New SKU
                  </Button>
                </Link>
              ) : view !== "all" ? (
                <Link to="/catalogue/skus?all=1">
                  <Button as="span" variant="outline" size="sm">
                    Show every SKU
                  </Button>
                </Link>
              ) : null
            }
          >
            {q
              ? "Nothing matched that search. Clear it to see the whole catalogue."
              : view === "active"
                ? "No SKU is active right now — every one has been deactivated."
                : view === "inactive"
                  ? "Every SKU is active. A deactivated one stays listed here, never removed."
                  : "Create the first one under an existing product."}
          </EmptyState>
        }
      />
    </div>
  );
}
